import { Box } from "@mui/material";
import { FormikProps } from "formik";
import { MouseEvent } from "react";
import { useSnackbar } from "@/hooks/useSnackbar";
import EncounterSign from "@/pages/app/provider/scheduling/appointment/encounter/EncounterSign";
import { Form2Payload } from "@/pages/app/provider/scheduling/appointment/encounter/encounter-constant";
import { EncounterSumaryFormTypeEnum } from "@/services";
import {
  findInvalidVitals,
  REQUIRED_SECTION_LABELS,
  REQUIRED_SECTIONS_ATTEMPT_STATUS,
  REQUIRED_SECTIONS_MISSING_STATUS,
  useRequiredSections,
} from "../../note-forms/initial-f2f";

export interface RequiredSectionsSignProps {
  formik: FormikProps<Form2Payload>;
  formType: EncounterSumaryFormTypeEnum;
  prefillEncounterSummery: unknown;
  label: string;
  navigateWithoutGuard: (callback: () => void) => void;
}

/** Save & Sign for Premier MD notes: blocked until every required section is answered and no vital shows an error. */
const RequiredSectionsSign = ({
  formik,
  formType,
  prefillEncounterSummery,
  label,
  navigateWithoutGuard,
}: RequiredSectionsSignProps) => {
  const showSnackbar = useSnackbar();
  const requirements = useRequiredSections({ ...formik.values, formType });
  const invalidVitals = findInvalidVitals(formik.values.noteContent);
  const signValidationMessage = !requirements.isComplete
    ? REQUIRED_SECTION_LABELS.signBlocked(
        requirements.missing.map((item) => item.title)
      )
    : invalidVitals.length
      ? REQUIRED_SECTION_LABELS.vitalsInvalid(invalidVitals)
      : undefined;
  // Each click snapshots the empty sections for the note's highlights. With gaps it stops here silently: the in-note banner already lists them.
  const guardSign = (event: MouseEvent<HTMLDivElement>) => {
    // Clicks in the portaled sign dialog and note preview still reach here through React; only the trigger's own clicks count.
    if (
      !(event.target instanceof Node) ||
      !event.currentTarget.contains(event.target)
    )
      return;
    const status = (formik.status ?? {}) as Record<string, unknown>;
    formik.setStatus({
      ...status,
      [REQUIRED_SECTIONS_MISSING_STATUS]: requirements.missing.map(
        (item) => item.key
      ),
      [REQUIRED_SECTIONS_ATTEMPT_STATUS]:
        (Number(status[REQUIRED_SECTIONS_ATTEMPT_STATUS]) || 0) + 1,
    });
    if (!signValidationMessage) return;
    event.stopPropagation();
    // Invalid vitals have no in-note banner, so the reason is shown as a toast.
    if (requirements.isComplete)
      showSnackbar({ message: signValidationMessage, type: "error" });
  };
  return (
    <Box onClickCapture={guardSign} display="contents">
      <EncounterSign
        prefillSummeryData={prefillEncounterSummery}
        label={label}
        onSave={() => formik.submitForm()}
        navigateWithoutGuard={navigateWithoutGuard}
        signValidationMessage={signValidationMessage}
      />
    </Box>
  );
};

export default RequiredSectionsSign;
